import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  FlaskConical,
  Play,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Code,
  Copy,
  Trash2,
  ChevronRight,
  Zap,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Clock },
  running: { label: 'Running', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: Zap },
  completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-500/10 text-red-400 border-red-500/30', icon: XCircle },
};

const TEST_TYPES = [
  { value: 'prompt', label: 'Prompt Test' },
  { value: 'rule', label: 'Rule Test' },
  { value: 'method', label: 'Method Test' },
  { value: 'template', label: 'Template Test' },
  { value: 'parser', label: 'Parser Test' },
  { value: 'e2e', label: 'End-to-End' },
];

const TestCard = ({ test, onView, onRun, onDelete }) => {
  const StatusIcon = STATUS_CONFIG[test.status]?.icon || Clock;

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all"
      data-testid={`sandbox-test-${test.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            test.status === 'completed' ? 'bg-emerald-500/20' :
            test.status === 'failed' ? 'bg-red-500/20' :
            test.status === 'running' ? 'bg-emerald-500/20' : 'bg-slate-700'
          }`}>
            <FlaskConical className={
              test.status === 'completed' ? 'text-emerald-400' :
              test.status === 'failed' ? 'text-red-400' :
              test.status === 'running' ? 'text-emerald-400' : 'text-slate-400'
            } size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{test.name}</h3>
            <p className="text-xs text-slate-500 capitalize">{test.test_type?.replace('_', ' ')} Test</p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[test.status]?.color}`}>
          {STATUS_CONFIG[test.status]?.label || test.status}
        </span>
      </div>

      {/* Execution Time */}
      {test.execution_time_ms && (
        <div className="flex items-center gap-2 mb-3 text-slate-400 text-sm">
          <Clock size={14} />
          <span>{test.execution_time_ms}ms</span>
        </div>
      )}

      {/* Confidence Score */}
      {test.confidence_score !== null && test.confidence_score !== undefined && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-slate-500">Confidence</span>
            <span className={
              test.confidence_score >= 0.8 ? 'text-emerald-400' :
              test.confidence_score >= 0.5 ? 'text-amber-400' : 'text-red-400'
            }>{(test.confidence_score * 100).toFixed(0)}%</span>
          </div>
          <div className="h-1.5 bg-stone-800 rounded-full overflow-hidden">
            <div 
              className={`h-full ${
                test.confidence_score >= 0.8 ? 'bg-emerald-500' :
                test.confidence_score >= 0.5 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${test.confidence_score * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Error Message */}
      {test.error_message && (
        <div className="flex items-start gap-2 mb-3 text-red-400 text-sm bg-red-500/10 rounded-lg p-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span className="line-clamp-2">{test.error_message}</span>
        </div>
      )}

      {/* Notes */}
      {test.notes && (
        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{test.notes}</p>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-800">
        <span className="text-xs text-slate-500">
          {new Date(test.created_at).toLocaleDateString()}
        </span>
        <div className="flex items-center gap-2">
          {test.status !== 'running' && (
            <Button size="sm" variant="ghost" onClick={() => onRun(test)}>
              <Play size={14} className="mr-1" />
              Run
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onView(test)}>
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

const CreateTestDialog = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    test_type: 'prompt',
    input_data: '{}',
    selected_prompt_id: '',
    selected_method_id: '',
    selected_template_id: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Test name is required');
      return;
    }
    let inputData;
    try {
      inputData = JSON.parse(formData.input_data);
    } catch (e) {
      toast.error('Invalid JSON in input data');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...formData,
        input_data: inputData,
      });
      toast.success('Test created successfully');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl bg-stone-900 border-stone-800 text-white">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FlaskConical className="text-amber-400" size={20} />
          Create Sandbox Test
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          Set up a new test to validate prompts, rules, methods, or templates.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Test Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Balance Dispute Prompt Test"
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Test Type</label>
            <Select
              value={formData.test_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, test_type: value }))}
            >
              <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-stone-800 border-slate-700">
                {TEST_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Linked Items */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Prompt ID</label>
            <Input
              value={formData.selected_prompt_id}
              onChange={(e) => setFormData(prev => ({ ...prev, selected_prompt_id: e.target.value }))}
              placeholder="prompt_xxx"
              className="bg-stone-800 border-slate-700 text-white font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Method ID</label>
            <Input
              value={formData.selected_method_id}
              onChange={(e) => setFormData(prev => ({ ...prev, selected_method_id: e.target.value }))}
              placeholder="method_xxx"
              className="bg-stone-800 border-slate-700 text-white font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Template ID</label>
            <Input
              value={formData.selected_template_id}
              onChange={(e) => setFormData(prev => ({ ...prev, selected_template_id: e.target.value }))}
              placeholder="tmpl_xxx"
              className="bg-stone-800 border-slate-700 text-white font-mono text-sm"
            />
          </div>
        </div>

        {/* Input Data */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Input Data (JSON)</label>
          <Textarea
            value={formData.input_data}
            onChange={(e) => setFormData(prev => ({ ...prev, input_data: e.target.value }))}
            placeholder='{"account_type": "collection", "balance": 5000}'
            className="bg-stone-800 border-slate-700 text-white font-mono text-sm resize-none"
            rows={6}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Notes</label>
          <Input
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Test description or expected outcome..."
            className="bg-stone-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
          Create Test
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const TestDetailDialog = ({ test, onClose, onRun }) => {
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    setRunning(true);
    try {
      await onRun(test);
      toast.success('Test executed');
    } catch (error) {
      toast.error('Test execution failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <DialogContent className="max-w-3xl bg-stone-900 border-stone-800 text-white max-h-[90vh]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FlaskConical className="text-amber-400" size={20} />
          {test.name}
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          {test.test_type?.replace('_', ' ')} Test - {STATUS_CONFIG[test.status]?.label}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
        {/* Status & Metrics */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-stone-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Status</p>
            <span className={`px-2 py-1 text-sm rounded ${STATUS_CONFIG[test.status]?.color}`}>
              {STATUS_CONFIG[test.status]?.label}
            </span>
          </div>
          <div className="bg-stone-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Execution Time</p>
            <p className="text-lg font-semibold text-white">
              {test.execution_time_ms ? `${test.execution_time_ms}ms` : 'N/A'}
            </p>
          </div>
          <div className="bg-stone-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Confidence</p>
            <p className="text-lg font-semibold text-white">
              {test.confidence_score !== null ? `${(test.confidence_score * 100).toFixed(0)}%` : 'N/A'}
            </p>
          </div>
          <div className="bg-stone-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Created</p>
            <p className="text-sm text-white">
              {new Date(test.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {test.error_message && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-red-400 mb-1">
              <AlertTriangle size={16} />
              <span className="font-medium">Error</span>
            </div>
            <p className="text-sm text-slate-300">{test.error_message}</p>
          </div>
        )}

        {/* Input Data */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-400">Input Data</p>
            <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(JSON.stringify(test.input_data, null, 2))}>
              <Copy size={14} className="mr-1" /> Copy
            </Button>
          </div>
          <pre className="bg-stone-950 rounded-lg p-3 text-xs text-slate-300 overflow-x-auto max-h-40">
            {JSON.stringify(test.input_data || {}, null, 2)}
          </pre>
        </div>

        {/* Output Result */}
        {test.output_result && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-400">Output Result</p>
              <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(JSON.stringify(test.output_result, null, 2))}>
                <Copy size={14} className="mr-1" /> Copy
              </Button>
            </div>
            <pre className="bg-stone-950 rounded-lg p-3 text-xs text-slate-300 overflow-x-auto max-h-40">
              {JSON.stringify(test.output_result, null, 2)}
            </pre>
          </div>
        )}

        {/* Output Preview */}
        {test.output_preview && (
          <div>
            <p className="text-sm text-slate-400 mb-2">Output Preview</p>
            <div className="bg-stone-950 rounded-lg p-3 text-sm text-slate-300 whitespace-pre-wrap max-h-60 overflow-y-auto">
              {test.output_preview}
            </div>
          </div>
        )}

        {/* Linked Items */}
        <div className="flex gap-4 flex-wrap">
          {test.selected_prompt_id && (
            <div className="bg-stone-800 rounded-lg px-3 py-2">
              <p className="text-xs text-slate-500">Prompt</p>
              <p className="text-sm font-mono text-slate-300">{test.selected_prompt_id}</p>
            </div>
          )}
          {test.selected_method_id && (
            <div className="bg-stone-800 rounded-lg px-3 py-2">
              <p className="text-xs text-slate-500">Method</p>
              <p className="text-sm font-mono text-slate-300">{test.selected_method_id}</p>
            </div>
          )}
          {test.selected_template_id && (
            <div className="bg-stone-800 rounded-lg px-3 py-2">
              <p className="text-xs text-slate-500">Template</p>
              <p className="text-sm font-mono text-slate-300">{test.selected_template_id}</p>
            </div>
          )}
        </div>

        {/* Notes */}
        {test.notes && (
          <div>
            <p className="text-sm text-slate-400 mb-1">Notes</p>
            <p className="text-sm text-slate-300 bg-stone-800 rounded-lg p-3">{test.notes}</p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={handleRun} disabled={running || test.status === 'running'}>
          {running ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
          Run Test
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const SandboxTesterPage = () => {
  const { getAuthHeaders } = useAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewingTest, setViewingTest] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const params = {};
      if (typeFilter !== 'all') params.test_type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await adminOpsService.listSandboxTests(params);
      setTests(data.tests || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load sandbox tests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, typeFilter, statusFilter]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const handleSave = async (formData) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    await adminOpsService.createSandboxTest(formData);
    fetchTests();
  };

  const handleRun = async (test) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    await adminOpsService.runSandboxTest(test.id);
    fetchTests();
    // Refresh viewing test if open
    if (viewingTest?.id === test.id) {
      const data = await adminOpsService.listSandboxTests({ test_type: test.test_type });
      const updated = data.tests?.find(t => t.id === test.id);
      if (updated) setViewingTest(updated);
    }
  };

  const filteredTests = tests.filter(test =>
    test.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    test.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="sandbox-tester-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FlaskConical className="text-amber-400" size={28} />
            Sandbox Tester
          </h1>
          <p className="text-slate-500 mt-1">
            Test and validate prompts, rules, methods, and templates in isolation
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus size={18} className="mr-2" />
          New Test
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tests..."
            className="pl-10 bg-stone-900 border-stone-800 text-white placeholder:text-slate-500"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800 text-white">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Types</SelectItem>
            {TEST_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-stone-900 border-stone-800 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchTests}>
          <RefreshCw size={18} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Total Tests</p>
          <p className="text-2xl font-bold text-white">{total}</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Completed</p>
          <p className="text-2xl font-bold text-emerald-400">
            {tests.filter(t => t.status === 'completed').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Failed</p>
          <p className="text-2xl font-bold text-red-400">
            {tests.filter(t => t.status === 'failed').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Pending</p>
          <p className="text-2xl font-bold text-slate-400">
            {tests.filter(t => t.status === 'pending').length}
          </p>
        </div>
      </div>

      {/* Tests Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48 bg-stone-800" />)}
        </div>
      ) : filteredTests.length === 0 ? (
        <div className="text-center py-16 bg-stone-900 rounded-xl border border-stone-800">
          <FlaskConical className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No Tests Found</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery ? 'No tests match your search.' : 'Create your first sandbox test to get started.'}
          </p>
          <Button onClick={() => setIsCreating(true)}>
            <Plus size={18} className="mr-2" />
            Create Test
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTests.map(test => (
            <TestCard
              key={test.id}
              test={test}
              onView={setViewingTest}
              onRun={handleRun}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <CreateTestDialog
          onSave={handleSave}
          onClose={() => setIsCreating(false)}
        />
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewingTest} onOpenChange={(open) => { if (!open) setViewingTest(null); }}>
        {viewingTest && (
          <TestDetailDialog
            test={viewingTest}
            onClose={() => setViewingTest(null)}
            onRun={handleRun}
          />
        )}
      </Dialog>
    </div>
  );
};

export default SandboxTesterPage;
